import { Link } from 'wouter';
import { BrainCircuit, GitFork, Play, FileText, ArrowRight, CheckCircle } from 'lucide-react';
import type { TabContent } from '@/types/cms';

const CONTENT_TYPE_BADGES: Record<string, { label: string; icon: typeof BrainCircuit }> = {
  'nera': { label: 'Ask Nera', icon: BrainCircuit },
  'decision-tree': { label: 'Interactive tool', icon: GitFork },
  'video': { label: 'Video', icon: Play },
  'pdf': { label: 'Document', icon: FileText },
};

interface CardGridItemProps {
  folderSlug: string;
  tab: TabContent;
  showVerifiedStamp?: boolean;
  variant?: 'vertical' | 'horizontal';
}

function formatVerifiedDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
}

function VerticalCard({ tab, showVerifiedStamp }: { tab: TabContent; showVerifiedStamp?: boolean }) {
  const Badge = CONTENT_TYPE_BADGES[tab.content_type];
  return (
    <article className="h-full bg-white border border-gray-200 rounded-xl overflow-hidden border-l-[3px] border-l-transparent group-hover:border-l-[#D5B13A] group-hover:shadow-md transition-all flex flex-col">
      {tab.og_image_url && (
        <div className="aspect-[16/9] w-full overflow-hidden bg-gray-100">
          <img
            src={tab.og_image_url}
            alt=""
            loading="lazy"
            className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500"
          />
        </div>
      )}

      <div className="p-5 lg:p-6 flex-1 flex flex-col">
        <div className="flex items-center gap-2 flex-wrap mb-3">
          {Badge && (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-semibold uppercase tracking-wider bg-[#2D7E32]/8 text-[#2D7E32] border border-[#2D7E32]/15">
              <Badge.icon className="h-3 w-3" />
              {Badge.label}
            </span>
          )}
          {showVerifiedStamp && tab.last_verified_at && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider bg-[#D5B13A]/10 text-[#D5B13A]/90 border border-[#D5B13A]/20">
              <CheckCircle className="h-2.5 w-2.5" />
              Verified {formatVerifiedDate(tab.last_verified_at)}
            </span>
          )}
        </div>

        <h3 className="font-heading text-xl lg:text-2xl font-extrabold text-[#2D7E32] leading-tight mb-2 line-clamp-2">
          {tab.label}
        </h3>

        {tab.summary && (
          <p className="text-sm lg:text-base text-gray-600 font-body leading-relaxed line-clamp-3 mb-4 flex-1">
            {tab.summary}
          </p>
        )}

        <div className="flex items-center gap-1.5 text-sm font-semibold text-[#D5B13A] opacity-70 group-hover:opacity-100 group-hover:gap-2.5 transition-all mt-auto">
          Read more
          <ArrowRight className="h-4 w-4" />
        </div>
      </div>
    </article>
  );
}

function HorizontalCard({ tab, showVerifiedStamp }: { tab: TabContent; showVerifiedStamp?: boolean }) {
  const Badge = CONTENT_TYPE_BADGES[tab.content_type];
  return (
    <article className="h-full bg-white border border-gray-200 rounded-xl overflow-hidden border-l-[3px] border-l-transparent group-hover:border-l-[#D5B13A] group-hover:shadow-md transition-all flex flex-col sm:flex-row">
      {tab.og_image_url && (
        <div className="sm:w-2/5 lg:w-1/3 flex-shrink-0 aspect-[16/9] sm:aspect-auto overflow-hidden bg-gray-100">
          <img
            src={tab.og_image_url}
            alt=""
            loading="lazy"
            className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500"
          />
        </div>
      )}

      <div className="p-6 lg:p-8 flex-1 flex flex-col sm:flex-row sm:items-center gap-6">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-3">
            {Badge && (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-semibold uppercase tracking-wider bg-[#2D7E32]/8 text-[#2D7E32] border border-[#2D7E32]/15">
                <Badge.icon className="h-3 w-3" />
                {Badge.label}
              </span>
            )}
            {showVerifiedStamp && tab.last_verified_at && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider bg-[#D5B13A]/10 text-[#D5B13A]/90 border border-[#D5B13A]/20">
                <CheckCircle className="h-2.5 w-2.5" />
                Verified {formatVerifiedDate(tab.last_verified_at)}
              </span>
            )}
          </div>

          <h3 className="font-heading text-xl lg:text-2xl xl:text-3xl font-extrabold text-[#2D7E32] leading-tight mb-2">
            {tab.label}
          </h3>

          {tab.summary && (
            <p className="text-sm lg:text-base text-gray-600 font-body leading-relaxed">
              {tab.summary}
            </p>
          )}
        </div>

        <div className="flex items-center gap-1.5 text-sm font-semibold text-[#D5B13A] opacity-70 group-hover:opacity-100 group-hover:gap-2.5 transition-all flex-shrink-0">
          Read more
          <ArrowRight className="h-4 w-4" />
        </div>
      </div>
    </article>
  );
}

export function CardGridItem({ folderSlug, tab, showVerifiedStamp, variant = 'vertical' }: CardGridItemProps) {
  return (
    <Link href={`/${folderSlug}/${tab.slug}`} className="group block h-full">
      {variant === 'horizontal' ? (
        <HorizontalCard tab={tab} showVerifiedStamp={showVerifiedStamp} />
      ) : (
        <VerticalCard tab={tab} showVerifiedStamp={showVerifiedStamp} />
      )}
    </Link>
  );
}
